(() => {
  if (typeof window === 'undefined') return;
  if (window.location.pathname.includes('/apps/gbb/easybundle')) return;

  const CART_CACHE_TTL_MS = 2500;
  const NOTICE_CLASS = 'arch-inventory-guard__notice';

  let cartCache = null;
  let cartCacheAt = 0;
  let cartFetchPromise = null;

  const parseIntSafe = (value) => {
    const parsed = Number.parseInt(String(value), 10);
    return Number.isFinite(parsed) ? parsed : 0;
  };

  const parseDatasetInventory = (value) => {
    if (value === undefined || value === null || value === '') return null;
    const parsed = Number.parseInt(String(value), 10);
    return Number.isFinite(parsed) ? Math.max(0, parsed) : null;
  };

  const getLocale = () =>
    String(window.Shopify?.locale || document.documentElement.lang || '').toLowerCase();

  const isEnglish = () => getLocale().startsWith('en');

  const pairLabel = (count) => {
    if (isEnglish()) return count > 1 ? 'pairs' : 'pair';
    return count > 1 ? 'paires' : 'paire';
  };

  const stockMessage = {
    soldOut() {
      if (isEnglish()) return 'There are no pairs left in this size.';
      return 'Il ne reste plus de paires disponibles pour cette taille.';
    },
    oneLeft() {
      if (isEnglish()) return 'Only 1 pair left in stock.';
      return 'Il ne reste qu’1 paire en stock.';
    },
    quantityLeft(count) {
      if (isEnglish()) return `Only ${count} ${pairLabel(count)} left in stock.`;
      return `Il ne reste que ${count} ${pairLabel(count)} en stock.`;
    },
  };

  const STOCK_MESSAGE_PATTERN =
    /épuisée|en stock|disponibilité|articles seulement|ajoutés à votre panier|Il ne reste|left in stock|pairs? left/i;

  const ArchInventoryGuard = {
    isStockRelatedMessage(message) {
      return Boolean(message && STOCK_MESSAGE_PATTERN.test(String(message)));
    },

    clearProductFormErrors(container) {
      const productForm =
        container?.closest?.('product-form') || document.querySelector('product-form');
      if (!productForm?.handleErrorMessage) return;
      productForm.handleErrorMessage();
      productForm.error = false;
    },

    setSubmitSoldOut(container, soldOut) {
      const productForm =
        container?.closest?.('product-form') || document.querySelector('product-form');
      if (!productForm) return;
      const submitBtn = productForm.querySelector('[type="submit"]');
      if (!submitBtn) return;

      const addLabel = (window.variantStrings?.addToCart || 'Ajouter au panier').toUpperCase();
      const soldOutLabel = (window.variantStrings?.soldOut || 'Épuisé').toUpperCase();
      const labelSpan = submitBtn.querySelector('span:not(.sold-out-message)');
      const soldOutSpan = submitBtn.querySelector('.sold-out-message');

      if (soldOut) {
        productForm.error = false;
        submitBtn.disabled = true;
        submitBtn.setAttribute('aria-disabled', 'true');
        if (labelSpan) {
          labelSpan.textContent = soldOutLabel;
          labelSpan.classList.remove('hidden');
        }
        if (soldOutSpan) soldOutSpan.classList.add('hidden');
      } else {
        submitBtn.disabled = false;
        submitBtn.removeAttribute('aria-disabled');
        if (labelSpan) {
          labelSpan.textContent = addLabel;
          labelSpan.classList.remove('hidden');
        }
        if (soldOutSpan) soldOutSpan.classList.add('hidden');
      }
    },

    refreshProductForm(container) {
      return new Promise((resolve) => {
        const productForm =
          container?.closest?.('product-form') || container || document.querySelector('product-form');
        if (!productForm) {
          resolve(true);
          return;
        }

        const quantityInput = productForm.querySelector('[name="quantity"]');
        const variantId =
          productForm.querySelector('[name="id"]')?.value
          || quantityInput?.dataset.variantId;

        this.applyQuantityLimits(productForm, null, variantId);

        setTimeout(async () => {
          const variant = this.getVariantFromInput(quantityInput);
          if (variantId) variant.id = variantId;
          const available = await this.getAvailableQty(variant, variantId, false);
          resolve(available === null || available > 0);
        }, 80);
      });
    },

    clearDrawerErrorsIfClosed() {
      const drawer = document.querySelector('cart-drawer');
      const isOpen =
        drawer?.classList.contains('active')
        || drawer?.classList.contains('is-open')
        || drawer?.hasAttribute('open');
      if (isOpen) return;
      const target =
        document.getElementById('CartDrawer-CartErrors') || document.getElementById('cart-errors');
      if (target) target.textContent = '';
    },
    shouldTrackInventory(variant) {
      if (!variant) return false;
      const management = variant.inventory_management || variant.inventoryManagement;
      const policy = variant.inventory_policy || variant.inventoryPolicy;
      if (management && management !== 'shopify') return false;
      if (policy === 'continue') return false;
      return true;
    },

    getInventoryQuantity(variant) {
      if (!variant) return null;
      const quantity = Number(
        variant.inventory_quantity !== undefined
          ? variant.inventory_quantity
          : variant.inventoryQuantity
      );
      return Number.isFinite(quantity) ? Math.max(0, quantity) : null;
    },

    getCartQtyForVariant(cart, variantId) {
      if (!cart || !Array.isArray(cart.items) || !variantId) return 0;
      const id = String(variantId);
      return cart.items.reduce((total, item) => {
        if (String(item.variant_id || item.id) === id) {
          return total + parseIntSafe(item.quantity);
        }
        return total;
      }, 0);
    },

    async fetchCart(force = false) {
      const now = Date.now();
      if (!force && cartCache && now - cartCacheAt < CART_CACHE_TTL_MS) {
        return cartCache;
      }

      if (!force && cartFetchPromise) return cartFetchPromise;

      cartFetchPromise = fetch(`${window.routes?.cart_url || '/cart'}.js`, {
        headers: { Accept: 'application/json' },
        credentials: 'same-origin',
      })
        .then((response) => {
          if (!response.ok) throw new Error('Cart unavailable');
          return response.json();
        })
        .then((cart) => {
          cartCache = cart;
          cartCacheAt = Date.now();
          return cart;
        })
        .finally(() => {
          cartFetchPromise = null;
        });

      return cartFetchPromise;
    },

    invalidateCartCache() {
      cartCache = null;
      cartCacheAt = 0;
    },

    getVariantFromInput(input) {
      if (!input) return null;
      return {
        id: input.dataset.variantId || input.dataset.quantityVariantId || '',
        inventory_management: input.dataset.inventoryManaged,
        inventory_policy: input.dataset.inventoryPolicy,
        inventory_quantity: parseDatasetInventory(input.dataset.inventoryQuantity),
        quantity_rule: {
          min: parseIntSafe(input.dataset.min || input.min || 1),
          max: input.dataset.max ? parseIntSafe(input.dataset.max) : null,
          increment: parseIntSafe(input.step || 1),
        },
      };
    },

    getRuleMax(variant, cartQty) {
      const ruleMax = variant?.quantity_rule?.max;
      if (ruleMax === null || ruleMax === undefined) return null;
      return Math.max(0, ruleMax - cartQty);
    },

    async getAvailableQty(variant, variantId, forceCart = false) {
      if (!this.shouldTrackInventory(variant)) return null;

      const inventoryQty = this.getInventoryQuantity(variant);
      if (inventoryQty === null) return null;

      const cart = await this.fetchCart(forceCart);
      const cartQty = this.getCartQtyForVariant(cart, variantId || variant.id);
      const stockLeft = Math.max(0, inventoryQty - cartQty);
      const ruleCap = this.getRuleMax(variant, cartQty);

      if (ruleCap === null) return stockLeft;
      return Math.min(stockLeft, ruleCap);
    },

    buildLimitMessage(availableQty) {
      if (availableQty <= 0) return stockMessage.soldOut();
      if (availableQty === 1) return stockMessage.oneLeft();
      return stockMessage.quantityLeft(availableQty);
    },

    async validateBeforeAdd(variantId, requestedQty, variantData = null) {
      const qty = Math.max(1, parseIntSafe(requestedQty));
      const variant = variantData || { id: variantId };
      const available = await this.getAvailableQty(variant, variantId, true);

      if (available === null) {
        return { ok: true, allowedQty: qty, message: '' };
      }

      if (available <= 0) {
        return { ok: false, allowedQty: 0, message: this.buildLimitMessage(0) };
      }

      if (qty > available) {
        return {
          ok: false,
          allowedQty: available,
          message: this.buildLimitMessage(available),
        };
      }

      return { ok: true, allowedQty: qty, message: '' };
    },

    async validateCartLineQuantity(input, requestedQty) {
      const qty = Math.max(0, parseIntSafe(requestedQty));
      const variant = this.getVariantFromInput(input);
      const variantId = input.dataset.quantityVariantId || input.dataset.variantId;

      if (!this.shouldTrackInventory(variant)) {
        return { ok: true, allowedQty: qty, message: '' };
      }

      const inventoryQty = this.getInventoryQuantity(variant);
      if (inventoryQty === null) {
        return { ok: true, allowedQty: qty, message: '' };
      }

      const cart = await this.fetchCart(true);
      const cartItem = typeof input.closest === 'function'
        ? input.closest('[data-cart-item-key]')
        : null;
      const itemKey = cartItem ? cartItem.dataset.cartItemKey : '';
      const line = Array.isArray(cart.items)
        ? cart.items.find((item) => item && item.key === itemKey)
        : null;
      const currentLineQty = line ? parseIntSafe(line.quantity) : 0;
      const totalForVariant = this.getCartQtyForVariant(cart, variantId);
      const otherLinesQty = Math.max(0, totalForVariant - currentLineQty);
      const maxForLine = Math.max(0, inventoryQty - otherLinesQty);
      const ruleMax = input.max ? parseIntSafe(input.max) : null;
      const cappedMax =
        ruleMax !== null && Number.isFinite(ruleMax) ? Math.min(maxForLine, ruleMax) : maxForLine;

      if (qty > cappedMax) {
        return {
          ok: false,
          allowedQty: cappedMax,
          message: this.buildLimitMessage(cappedMax),
        };
      }

      return { ok: true, allowedQty: qty, message: '' };
    },

    validateVariantTargetQuantity(input, requestedQty) {
      const qty = Math.max(0, parseIntSafe(requestedQty));
      const variant = this.getVariantFromInput(input);
      const management = variant?.inventory_management || variant?.inventoryManagement;
      const policy = variant?.inventory_policy || variant?.inventoryPolicy;

      if (management !== 'shopify' || policy === 'continue') {
        return { ok: true, allowedQty: qty, message: '' };
      }

      const inventoryQty = this.getInventoryQuantity(variant);
      if (inventoryQty === null) {
        return { ok: true, allowedQty: qty, message: '' };
      }

      const ruleMax = input.max ? parseIntSafe(input.max) : null;
      const cap = ruleMax !== null && Number.isFinite(ruleMax) ? Math.min(inventoryQty, ruleMax) : inventoryQty;

      if (qty > cap) {
        return {
          ok: false,
          allowedQty: cap,
          message: this.buildLimitMessage(cap),
        };
      }

      return { ok: true, allowedQty: qty, message: '' };
    },

    async validateForm(form) {
      const variantId = form.querySelector('[name="id"]')?.value;
      const quantityInput = form.querySelector('[name="quantity"]');
      const requestedQty = quantityInput ? quantityInput.value : 1;
      const variant = this.getVariantFromInput(quantityInput);
      if (variantId) variant.id = variantId;
      return this.validateBeforeAdd(variantId, requestedQty, variant);
    },

    showDrawerError(message) {
      if (!message) return;
      const drawer = document.querySelector('cart-drawer');
      const isOpen =
        drawer?.classList.contains('active')
        || drawer?.classList.contains('is-open')
        || drawer?.hasAttribute('open');
      if (!isOpen) return;
      const target =
        document.getElementById('CartDrawer-CartErrors') || document.getElementById('cart-errors');
      if (target) {
        target.textContent = message;
        target.setAttribute('role', 'alert');
      }
    },

    ensureNotice(container) {
      if (!container) return null;
      let notice = container.querySelector(`.${NOTICE_CLASS}`);
      if (!notice) {
        notice = document.createElement('p');
        notice.className = NOTICE_CLASS;
        notice.setAttribute('role', 'status');
        notice.setAttribute('aria-live', 'polite');
        const quantityRow =
          container.querySelector('.arch-quantity-row')
          || container.querySelector('.product-form__quantity')
          || container.querySelector('quantity-input')?.parentElement;
        if (quantityRow) {
          quantityRow.insertAdjacentElement('afterend', notice);
        } else {
          container.appendChild(notice);
        }
      }
      return notice;
    },

    setNotice(container, message) {
      const notice = this.ensureNotice(container);
      if (!notice) return;
      if (!message) {
        notice.remove();
        return;
      }
      notice.textContent = message;
    },

    togglePlusButton(quantityInput, disabled) {
      if (!quantityInput) return;
      const root = quantityInput.closest('quantity-input');
      if (!root) return;
      const plusButton = root.querySelector('[name="plus"]');
      if (!plusButton) return;
      plusButton.classList.toggle('disabled', disabled);
      plusButton.toggleAttribute('disabled', disabled);
    },

    applyQuantityLimits(container, variant, variantId, options = {}) {
      if (!container) return;

      const quantityInput = container.querySelector('[name="quantity"]');
      if (!quantityInput) return;

      const sync = async () => {
        this.clearProductFormErrors(container);
        this.clearDrawerErrorsIfClosed();

        const available = await this.getAvailableQty(
          variant || this.getVariantFromInput(quantityInput),
          variantId || quantityInput.dataset.variantId || quantityInput.dataset.quantityVariantId,
          Boolean(options.forceCart)
        );

        if (available === null) {
          this.setNotice(container, '');
          this.togglePlusButton(quantityInput, false);
          this.setSubmitSoldOut(container, false);
          return;
        }

        const min = parseIntSafe(quantityInput.min || quantityInput.dataset.min || 1);
        let max = available > 0 && available >= min ? available : 0;

        const ruleMax = quantityInput.dataset.max ? parseIntSafe(quantityInput.dataset.max) : null;
        if (ruleMax !== null && Number.isFinite(ruleMax)) {
          max = Math.min(max, ruleMax);
        }

        if (max > 0) {
          quantityInput.max = String(max);
        } else {
          quantityInput.removeAttribute('max');
        }

        const current = parseIntSafe(quantityInput.value);
        if (max > 0 && current > max) {
          quantityInput.value = String(max);
        }

        const atLimit = max <= 0 || current >= max;
        this.togglePlusButton(quantityInput, atLimit);

        if (max <= 0) {
          this.setNotice(container, stockMessage.soldOut());
          if (parseIntSafe(quantityInput.value) < 1) quantityInput.value = '1';
        } else if (atLimit) {
          this.setNotice(container, this.buildLimitMessage(max));
        } else {
          this.setNotice(container, '');
        }

        this.setSubmitSoldOut(container, max <= 0);
      };

      sync().catch(() => {});
    },

    syncProductForm(productForm, variant) {
      if (!productForm) return;
      const quantityInput = productForm.querySelector('[name="quantity"]');
      if (!quantityInput || !variant) return;

      const nextVariantId = String(variant.id || '');
      const previousVariantId = quantityInput.dataset.variantId || '';
      const inventoryQuantity = this.getInventoryQuantity(variant);

      quantityInput.dataset.variantId = nextVariantId;
      quantityInput.dataset.inventoryManaged =
        variant.inventory_management || variant.inventoryManagement || '';
      quantityInput.dataset.inventoryPolicy = variant.inventory_policy || variant.inventoryPolicy || '';

      if (inventoryQuantity !== null) {
        quantityInput.dataset.inventoryQuantity = String(inventoryQuantity);
      } else if (previousVariantId !== nextVariantId) {
        delete quantityInput.dataset.inventoryQuantity;
      }

      this.applyQuantityLimits(productForm, variant, nextVariantId);
    },

    bindProductForm(productForm) {
      if (!productForm || productForm.dataset.archInventoryGuardBound === 'true') return;
      productForm.dataset.archInventoryGuardBound = 'true';

      const quantityInput = productForm.querySelector('[name="quantity"]');
      if (quantityInput) {
        quantityInput.addEventListener('change', () => {
          this.applyQuantityLimits(productForm);
        });
      }

      this.applyQuantityLimits(productForm);
    },

    scanProductForms(root = document) {
      root.querySelectorAll('product-form').forEach((form) => this.bindProductForm(form));
    },

    init() {
      this.scanProductForms();

      window.addEventListener('pageshow', () => {
        this.invalidateCartCache();
        document.querySelectorAll('product-form').forEach((form) => {
          this.applyQuantityLimits(form);
        });
      });

      document.addEventListener('variant:changed', (event) => {
        const detail = event.detail || {};
        const sectionId = detail.sectionId;
        const variant = detail.variant;
        if (!sectionId || !variant) return;

        const productInfo = document.querySelector(`#MainProduct-${sectionId}`) || document.querySelector('product-info');
        const productForm = productInfo?.querySelector('product-form');
        if (productForm) {
          this.syncProductForm(productForm, variant);
        }
      });

      document.addEventListener('product-info:loaded', (event) => {
        const productInfo = event.target;
        if (productInfo) this.scanProductForms(productInfo);
      });

      if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
        subscribe(PUB_SUB_EVENTS.cartUpdate, () => {
          this.invalidateCartCache();
          document.querySelectorAll('product-form').forEach((form) => {
            this.refreshProductForm(form);
          });
        });

        subscribe(PUB_SUB_EVENTS.variantChange, (event) => {
          const variant = event?.data?.variant;
          const sectionId = event?.data?.sectionId;
          if (!variant || !sectionId) return;

          const productInfo =
            document.querySelector(`product-info[data-section="${sectionId}"]`)
            || document.getElementById(`MainProduct-${sectionId}`);
          const productForm = productInfo?.querySelector('product-form');
          if (productForm) this.syncProductForm(productForm, variant);
        });
      }

      const observer = new MutationObserver((mutations) => {
        for (const mutation of mutations) {
          mutation.addedNodes.forEach((node) => {
            if (!(node instanceof Element)) return;
            if (node.matches?.('product-form')) this.bindProductForm(node);
            if (node.querySelectorAll) this.scanProductForms(node);
          });
        }
      });

      observer.observe(document.documentElement, { childList: true, subtree: true });
    },
  };

  window.ArchInventoryGuard = ArchInventoryGuard;

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => ArchInventoryGuard.init());
  } else {
    ArchInventoryGuard.init();
  }
})();
